// Register at speech start: final transcripts can arrive in a different order.
// Each command reads the UI left by the preceding command when it executes.
export function createVoiceCommandTurns({getForm, execute, isAlive, onUser, onResult, onPreview, onPending, settle = async () => {}}) {
  const turns = new Map();
  let queue = Promise.resolve(), epoch = 0, disposed = false, pending = 0, latest;
  const alive = () => !disposed && isAlive();
  const publishPending = () => { if (alive()) onPending(pending); };
  function cancel(message = 'Pending commands canceled. Any request already sent remains submitted.') {
    epoch++;
    for (const turn of turns.values()) {
      clearTimeout(turn.timer);
      if (!turn.done) { turn.done = true; turn.resolve(null); }
    }
    pending = 0;
    if (alive()) { onPreview(''); publishPending(); if (message) onResult(message); }
  }
  function begin(id) {
    if (!id || turns.has(id) || !alive()) return;
    if (pending >= 20) { cancel('Too many commands are waiting. Please repeat your next request.'); return; }
    const currentEpoch = epoch;
    let resolve;
    const ready = new Promise(done => { resolve = done; });
    const turn = {resolve, done:false, form:getForm?.(), partial:'', epoch:currentEpoch};
    turns.set(id, turn); latest = id; pending++; onPreview(''); publishPending();
    const isCurrent = () => alive() && epoch === currentEpoch;
    const run = async () => {
      const text = await ready;
      if (!isCurrent() || !text) return;
      onUser(text, id);
      try {
        const result = await execute(text, {id, form:turn.form, isCurrent});
        if (isCurrent() && result) onResult(result, id);
      } catch (error) {
        if (isCurrent()) onResult(error.message || 'That command could not finish. Please try again.', id);
      } finally {
        // Give React a chance to publish the next screen before reading its context.
        await settle();
        if (isCurrent()) { pending--; publishPending(); }
      }
    };
    queue = queue.then(run, run);
    if (turns.size > 200) { const first = turns.keys().next().value; if (turns.get(first).done) turns.delete(first); }
  }
  function complete(id, text) {
    const turn = turns.get(id);
    if (!turn || turn.done || turn.epoch !== epoch || !alive()) return;
    const value = typeof text === 'string' ? text.trim().slice(0, 4000) : '';
    if (/^(?:please )?(?:stop|cancel|never ?mind|clear (?:the )?(?:queue|commands)|cancel (?:all |pending )?commands)[.!?]*$/i.test(value)) {
      onUser(value, id); cancel(); return;
    }
    if (!value) { cancel('I could not hear a complete command. Pending commands were canceled; please repeat your request.'); return; }
    clearTimeout(turn.timer); turn.done = true;
    if (latest === id) onPreview(value);
    turn.resolve(value);
  }
  return {
    begin, complete, cancel,
    stop(id) { const turn=turns.get(id); if (turn && !turn.done && !turn.timer) turn.timer=setTimeout(() => complete(id, null), 12000); },
    fail(id) { complete(id, null); },
    delta(id, text) { const turn=turns.get(id); if (turn && !turn.done && turn.epoch===epoch && alive()) { turn.partial=(turn.partial+text).slice(0,4000); if (latest===id) onPreview(turn.partial); } },
    dispose() { cancel(''); disposed = true; },
    get pending() { return pending; }
  };
}
